"use client";

import { useEffect, useState } from "react";
import { CLOCK_TICK_MS } from "../_logic/constants";
import { scheduleNow } from "../_logic/utils/schedule-clock";

/**
 * Ticking-clock hook. Returns the current time as ms since epoch, re-rendering the caller
 * every `intervalMs`.
 *
 * Call this **inside** the component subtree that needs the clock (e.g. the live banner or
 * the calendar now-line) — never high in the page tree — so a tick re-renders only that
 * subtree and not unrelated sections. The interval is cleared on unmount.
 *
 * SSR note: the initial value is read once at mount. Components that render time-derived
 * text should guard on a mounted flag to avoid a hydration mismatch.
 *
 * @param intervalMs - Tick interval in ms. Defaults to `CLOCK_TICK_MS` (minute resolution).
 * @param enabled - When false, the interval is paused and `now` freezes at its last value. Use to
 *   stop ticking once a clock is no longer meaningful (e.g. a countdown that has reached zero).
 * @returns The current epoch time in ms, updated each tick.
 */
export function useNow(intervalMs: number = CLOCK_TICK_MS, enabled: boolean = true): number {
  const [now, setNow] = useState<number>(() => scheduleNow());

  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(() => setNow(scheduleNow()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, enabled]);

  return now;
}

/**
 * Like {@link useNow}, but returns `null` until the component has mounted on the client. Use this
 * for any UI whose output depends on the current time, so the server render and the first client
 * render agree (no hydration mismatch) — the real time arrives after mount. Owning the mounted
 * guard here keeps every time-derived consumer from re-implementing it.
 *
 * @param intervalMs - Tick interval in ms. Defaults to `CLOCK_TICK_MS`.
 * @param enabled - When false, the interval is paused and `now` freezes at its last value.
 * @returns The current epoch time in ms, or `null` before the component has mounted.
 */
export function useClientNow(intervalMs: number = CLOCK_TICK_MS, enabled: boolean = true): number | null {
  const now = useNow(intervalMs, enabled);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return mounted ? now : null;
}
